/**
 * Attaching items to another one ("Attach to…": a triage area's row, or a selection): which item, searched for, and
 * where on it they go: its attachments, or one of its link fields that can hold them.
 */
import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { ItemDetail, ObjEntity, Field as SchemaField, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { ATTACHMENTS } from "../../lib/attach.ts";
import { patchItem } from "../../lib/data.ts";
import { spaceApi } from "../../lib/paths.ts";
import { useAction } from "../../lib/resource.ts";
import { Button } from "../../ui/Button.tsx";
import { Combobox } from "../../ui/Combobox.tsx";
import { DialogActions } from "../../ui/Dialog.tsx";
import { Field, Form, Select } from "../../ui/form.tsx";
import { type ItemOption, searchItems } from "./search.tsx";

/** Adds `ids` to `target`'s refs under `key` (what's there already stays once; a single link's replaced), based on its current version. */
async function attachAll(space: SpaceOut, target: string, ids: readonly string[], to: { key: string; many: boolean }) {
  const { key, many } = to;
  const item = await api.get<ItemDetail>(spaceApi(space, `/items/${target}`));
  const list = many ? (item.refs?.[key] ?? []) : [];
  const had = new Set(list.map((x) => (typeof x === "string" ? x : x.id)));
  await patchItem(space, item, { refs: { [key]: [...list, ...ids.filter((i) => !had.has(i))] } });
}

/**
 * The link fields of `target`'s entity that can hold items of `entity` (one entity's, or, `undefined`, any): those
 * linking to that entity or to any item. One holding a single link only when there's a single item to put there.
 */
function linkFields(target: ObjEntity | undefined, entity: string | null | undefined, count: number): SchemaField[] {
  return (target?.fields ?? []).filter(
    (f) =>
      f.type === "ref" &&
      f.key !== ATTACHMENTS &&
      (!f.target || (entity !== undefined && f.target === entity)) &&
      (f.many || count === 1),
  );
}

/** A field's name: its label; without one, its key in words (`due_date`: "Due date"), never the key itself. */
function nameOf(f: SchemaField): string {
  if (f.label) {
    return f.label;
  }
  const words = f.key.replaceAll("_", " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Which item to attach `ids` to (not one of them), and where on it. `entity`: theirs, when they share one (a row's);
 * `undefined` when it isn't known (a selection). `onDone` says what was done (`to`: the item they went to).
 */
export function AttachForm(p: {
  space: SpaceOut;
  ids: readonly string[];
  entity?: string | null | undefined;
  entities: readonly ObjEntity[];
  onClose: () => void;
  onDone: (to: ItemOption) => void;
}) {
  const [target, setTarget] = useState<ItemOption | null>(null);
  const [key, setKey] = useState(ATTACHMENTS);
  const fields = linkFields(
    p.entities.find((e) => e.id === target?.entity),
    p.entity,
    p.ids.length,
  );
  const field = fields.find((f) => f.key === key);
  const [run, busy] = useAction(async (to: ItemOption) => {
    await attachAll(
      p.space,
      to.value,
      p.ids,
      field ? { key, many: field.many ?? false } : { key: ATTACHMENTS, many: true },
    );
    p.onClose();
    p.onDone(to);
  });
  return (
    <Form
      class="flex flex-col gap-4"
      onSubmit={() => {
        if (target) {
          run(target);
        }
      }}
    >
      <Field
        group={true}
        label={t("triage.target")}
        hint={target ? t("triage.selected", { title: target.label }) : null}
      >
        <Combobox
          label={t("triage.target")}
          placeholder={t("common.searchItems")}
          search={async (q, signal) => (await searchItems(p.space, q, signal)).filter((o) => !p.ids.includes(o.value))}
          onSelect={(o) => {
            setTarget(o);
            setKey(ATTACHMENTS);
          }}
        />
      </Field>
      {/* Where on it: its attachments, or (picked, with link fields that can hold them) one of those. */}
      {fields.length > 0 ? (
        <Field label={t("attach.under")}>
          <Select
            value={key}
            onValue={setKey}
            options={[
              { value: ATTACHMENTS, label: t("attachments.title") },
              ...fields.map((f) => ({ value: f.key, label: nameOf(f) })),
            ]}
          />
        </Field>
      ) : null}
      <DialogActions>
        <Button answer="cancel" onClick={p.onClose}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" submit={true} busy={busy} disabled={target === null}>
          {t("triage.file")}
        </Button>
      </DialogActions>
    </Form>
  );
}
